import { useState, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Search, X } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const location = useLocation();

  const toggleMenu = () => {
    setIsOpen((prev) => !prev);
  };

  const navLinkClasses = ({ isActive }) =>
    isActive
      ? 'no-underline font-bold pt-2 text-black pb-1 relative after:content-[""] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-3/4 after:h-[2px] after:bg-black'
      : 'no-underline text-gray-600 pt-2 hover:text-black pb-1 relative after:content-[""] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[2px] after:bg-gray-300 hover:after:w-3/4 after:transition-all after:duration-200';

  // Custom function to check if Living Room is active (handles both /livingroom and /living-room)
  const isLivingRoomActive = location.pathname === '/livingroom' || 
                             location.pathname === '/living-room' || 
                             location.pathname.startsWith('/livingroom/') || 
                             location.pathname.startsWith('/living-room/');

  const livingRoomClasses = isLivingRoomActive
    ? 'no-underline font-bold pt-2 text-black pb-1 relative after:content-[""] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-3/4 after:h-[2px] after:bg-black'
    : 'no-underline text-gray-600 pt-2 hover:text-black pb-1 relative after:content-[""] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[2px] after:bg-gray-300 hover:after:w-3/4 after:transition-all after:duration-200';

  const toggleSearch = () => {
    setIsSearchOpen((prev) => !prev);
    setTimeout(() => {
      if (!isSearchOpen && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    closeSearch();
  };

  return (
    <header className="sticky top-0 z-50 bg-white font-serif shadow-sm">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 flex justify-between items-center">
        <NavLink to="/" className="flex items-center no-underline">
          <img
            src="/assets/logo.png"
            alt="ModernFurniture Logo"
            className="w-20 sm:w-24 md:w-28 lg:w-32"
          />
        </NavLink>
        <nav className="hidden lg:flex space-x-4 xl:space-x-6">
          <NavLink to="/livingroom" className={livingRoomClasses}>
            Living Room
          </NavLink>
          <NavLink to="/bedroom" className={navLinkClasses}>
            Bedroom
          </NavLink>
          <NavLink to="/restaurant" className={navLinkClasses}>
            Restaurant
          </NavLink>
          <NavLink to="/bar" className={navLinkClasses}>
            Bar
          </NavLink>
          <NavLink to="/cafe" className={navLinkClasses}>
            Cafe
          </NavLink>
          <NavLink to="/about" className={navLinkClasses}>
            About Us
          </NavLink>
          <NavLink to="/contact" className={navLinkClasses}>
            Contact
          </NavLink>

          <div className="flex items-start">
            <div className="relative">
              <button
                onClick={toggleSearch}
                className={`pt-2.5 rounded-full text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-300 ${isSearchOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
                  }`}
                aria-label="Search"
              >
                <Search className="h-5 w-5" />
              </button>

              <div
                className={`absolute left-0 top-0 transition-all duration-300 ease-in-out ${isSearchOpen
                  ? 'w-64 xl:w-80 opacity-100 pointer-events-auto'
                  : 'w-0 opacity-0 pointer-events-none'
                  }`}
              >
                <form onSubmit={handleSearchSubmit} className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent bg-white shadow-sm"
                  />
                  <button
                    type="button"
                    onClick={closeSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </nav>
        <div className="lg:hidden">
          <button
            onClick={toggleMenu}
            className="text-gray-600 hover:text-black focus:outline-none p-2"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden px-3 sm:px-4 pb-4 bg-white border-t border-gray-100">
          <nav>
            <ul className="flex flex-col space-y-3 sm:space-y-4 mt-4">
              <li>
                <NavLink
                  to="/livingroom"
                  onClick={() => setIsOpen(false)}
                  className={livingRoomClasses}
                >
                  Living Room
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/bedroom"
                  onClick={() => setIsOpen(false)}
                  className={navLinkClasses}
                >
                  Bedroom
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/restaurant"
                  onClick={() => setIsOpen(false)}
                  className={navLinkClasses}
                >
                  Restaurant
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/bar"
                  onClick={() => setIsOpen(false)}
                  className={navLinkClasses}
                >
                  Bar
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/cafe"
                  onClick={() => setIsOpen(false)}
                  className={navLinkClasses}
                >
                  Cafe
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/about"
                  onClick={() => setIsOpen(false)}
                  className={navLinkClasses}
                >
                  About Us
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className={navLinkClasses}
                >
                  Contact
                </NavLink>
              </li>
            </ul>
          </nav>
          <div className="mt-4 flex flex-col space-y-3 sm:space-y-4">
            <form onSubmit={handleSearchSubmit} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="border border-gray-300 rounded-lg p-2 sm:p-3 w-full text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-gray-500"
              />
            </form>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
